import * as React from "react";
import {
  Box,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Divider,
  Typography,
  Avatar,
  Badge,
  IconButton,
  Drawer,
  useMediaQuery,
  useTheme,
  AppBar,
  Toolbar,
} from "@mui/material";
import { useNavigate, useLocation } from "react-router-dom";

// Icons
import MenuIcon from "@mui/icons-material/Menu";
import LogoutIcon from "@mui/icons-material/Logout";
import DashboardIcon from "@mui/icons-material/Dashboard";
import PeopleAltIcon from "@mui/icons-material/PeopleAlt";
import SupervisedUserCircleIcon from "@mui/icons-material/SupervisedUserCircle";
import ContactPhoneIcon from "@mui/icons-material/ContactPhone";
import ContactMailIcon from "@mui/icons-material/ContactMail";
import AssignmentIndIcon from "@mui/icons-material/AssignmentInd";
import SettingsApplicationsIcon from "@mui/icons-material/SettingsApplications";
import NotificationsIcon from "@mui/icons-material/Notifications";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";

import { AuthContext } from "../../AuthContext.js";
import fds_logo from "../../Images/fdc7.png";
import { enquiryService } from "../../services/enquiryService";
import { prospectService } from "../../services/prospectService";
import { notificationService } from "../../services/notificationService";

const NavItem = React.memo(({ icon, label, path, badge = 0, childPaths = [], currentPath, onNavigate }) => {
  const isActive =
    currentPath === path ||
    childPaths.some(
      (cp) => currentPath.toLowerCase() === cp.toLowerCase(),
    );

  return (
    <ListItem
      disablePadding
      onClick={() => onNavigate(path)}
      sx={{ mb: 1 }}
    >
      <ListItemButton
        sx={{
          borderRadius: "12px",
          mx: 1,
          transition: "background 0.3s ease, transform 0.2s ease",
          background: isActive
            ? "linear-gradient(135deg, #9e1c29 0%, #eb1d27 100%)"
            : "transparent",
          "&:hover": {
            backgroundColor: isActive ? "" : "rgba(255, 255, 255, 0.08)",
            transform: "translateX(3px)",
          },
        }}
      >
        <ListItemIcon sx={{ color: "white", minWidth: "45px" }}>
          <Badge badgeContent={badge} color="error">
            {icon}
          </Badge>
        </ListItemIcon>
        <ListItemText
          primary={label}
          primaryTypographyProps={{
            fontSize: "0.95rem",
            fontWeight: isActive ? 600 : 400,
          }}
        />
      </ListItemButton>
    </ListItem>
  );
});

function Sidebar() {
  const { clearAuthData } = React.useContext(AuthContext);
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const [isSuperAdmin, setIsSuperAdmin] = React.useState(
    () => localStorage.getItem("superadmin") === "true"
  );

  React.useEffect(() => {
    const handler = () =>
      setIsSuperAdmin(localStorage.getItem("superadmin") === "true");
    window.addEventListener("superadminToggled", handler);
    return () => window.removeEventListener("superadminToggled", handler);
  }, []);

  // Responsive check: true if screen is smaller than 900px (md)
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [mobileOpen, setMobileOpen] = React.useState(false);

  const [profileImageLink, setprofileImageLink] = React.useState(
    "https://mui.com/static/images/avatar/2.jpg",
  );
  const [profileUserName, setProfileUserName] = React.useState("");
  const [pendingEnquiriesCount, setPendingEnquiriesCount] = React.useState(0);
  const [pendingProspectsCount, setPendingProspectsCount] = React.useState(0);

  const handleDrawerToggle = () => setMobileOpen(!mobileOpen);

  React.useEffect(() => {
    setprofileImageLink(localStorage.getItem("profileImg"));
    const name = localStorage.getItem("username") || "";
    setProfileUserName(name.charAt(0).toUpperCase() + name.slice(1));
  }, []);

  const fetchPendingEnquiriesCount = React.useCallback(async () => {
    try {
      const result = await enquiryService.getEnquiries();
      if (!result || result.success === false) {
        setPendingEnquiriesCount(0);
        return;
      }

      const list = Array.isArray(result.data)
        ? result.data
        : Array.isArray(result.data?.data)
        ? result.data.data
        : [];

      const count = list.filter(
        (enquiry) => (enquiry.status || "").toLowerCase() === "new",
      ).length;
      setPendingEnquiriesCount(count);
    } catch (e) {
      console.error("Failed to fetch enquiries for sidebar badge", e);
      setPendingEnquiriesCount(0);
    }
  }, []);

  const fetchPendingProspectsCount = React.useCallback(async () => {
    try {
      const isAdmin =
        localStorage.getItem("roleId") === "af7c1fe6-d669-414e-b066-e9733f0de7a8";
      const salesExec = isAdmin ? "" : localStorage.getItem("fullName") || "";
      const result = await prospectService.getPendingCount(salesExec);
      if (result && result.success) {
        setPendingProspectsCount(Number(result.count) || 0);
      } else {
        setPendingProspectsCount(0);
      }
    } catch (e) {
      console.error("Failed to fetch prospects count", e);
      setPendingProspectsCount(0);
    }
  }, []);

  React.useEffect(() => {
    fetchPendingEnquiriesCount();
    fetchPendingProspectsCount();
    // Initialize push notifications after login
    notificationService.init();
  }, [fetchPendingEnquiriesCount, fetchPendingProspectsCount]);

  React.useEffect(() => {
    const handler = () => {
      fetchPendingEnquiriesCount();
    };
    window.addEventListener("enquiriesUpdated", handler);
    return () => {
      window.removeEventListener("enquiriesUpdated", handler);
    };
  }, [fetchPendingEnquiriesCount]);

  const logout = async () => {
    localStorage.clear();
    await clearAuthData();
    navigate("/");
  };

  const handleNavigate = React.useCallback((path) => {
    navigate(path);
    if (isMobile) setMobileOpen(false);
  }, [navigate, isMobile]);

  const currentPath = location.pathname;

  const drawerContent = (
    <Box
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        background: "#141414",
        color: "white",
      }}
    >
      {/* Top Header */}
      <Box
        sx={{
          p: 3,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <img
            src={fds_logo}
            alt="Logo"
            onClick={() => navigate("/")}
            style={{ height: "35px", cursor: "pointer" }}
          />
          {isSuperAdmin && (
            <AdminPanelSettingsIcon
              sx={{ color: "#eb1d27", fontSize: 24 }}
            />
          )}
        </Box>
      </Box>

      {/* Profile Card */}
      <Box
        sx={{
          mx: 2,
          mb: 2,
          p: 2,
          borderRadius: "16px",
          backgroundColor: "rgba(255,255,255,0.05)",
          display: "flex",
          alignItems: "center",
        }}
      >
        <Avatar
          src={profileImageLink}
          sx={{ width: 40, height: 40, border: "2px solid #eb1d27" }}
        />
        <Box sx={{ ml: 2, overflow: "hidden" }}>
          <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.5)" }}>
            Welcome back,
          </Typography>
          <Typography variant="body1" noWrap sx={{ fontWeight: 600 }}>
            {profileUserName || "User"}
          </Typography>
        </Box>
      </Box>

      <Divider sx={{ borderColor: "rgba(255,255,255,0.1)", mx: 2, mb: 1 }} />

      {/* Navigation */}
      <Box sx={{ flexGrow: 1, overflowY: "auto", px: 1 }}>
        <List>
          <NavItem icon={<DashboardIcon />} label="Feed" path="/Dashboard" currentPath={currentPath} onNavigate={handleNavigate} />
          <NavItem icon={<PeopleAltIcon />} label="Leads" path="/Leads" currentPath={currentPath} onNavigate={handleNavigate} />
          <NavItem
            icon={<SupervisedUserCircleIcon />}
            label="Customers"
            path="/Customers"
            currentPath={currentPath}
            onNavigate={handleNavigate}
          />
          <NavItem
            icon={<ContactPhoneIcon />}
            label="Call Logs"
            path="/callLogs"
            currentPath={currentPath}
            onNavigate={handleNavigate}
          />
          <NavItem
            icon={<AssignmentIndIcon />}
            label="Prospects"
            path="/prospects"
            badge={pendingProspectsCount}
            currentPath={currentPath}
            onNavigate={handleNavigate}
          />
          {localStorage.getItem("roleId") ===
            "af7c1fe6-d669-414e-b066-e9733f0de7a8" && (
            <NavItem
              icon={<SettingsApplicationsIcon />}
              label="Admin Panel"
              path="/adminPanel"
              currentPath={currentPath}
              onNavigate={handleNavigate}
              childPaths={[
                "/Users",
                "/VehicleModels",
                "/carFacts",
                "/reports",
                "/closingReasons",
                "/Inventory",
                "/blogs",
                "/blogs/create",
                "/blogs/edit",
                "/AddCar",
                "/admin/enquiries",
                "/admin/prospects",
                "/admin/push-notifications",
              ]}
              badge={pendingEnquiriesCount}
            />
          )}
        </List>
      </Box>

      {/* Footer */}
      <Box sx={{ p: 2, backgroundColor: "rgba(0,0,0,0.2)" }}>
        <ListItemButton
          onClick={logout}
          sx={{
            borderRadius: "12px",
            color: "#ff4d4d",
            "&:hover": { backgroundColor: "#BA0021", color: "white" },
          }}
        >
          <ListItemIcon sx={{ color: "inherit", minWidth: "40px" }}>
            <LogoutIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="Logout" />
        </ListItemButton>
        <Box
          onClick={() => navigate("/changeLogs")}
          sx={{
            mt: 2,
            display: "flex",
            alignItems: "center",
            gap: 1,
            px: 2,
            cursor: "pointer",
            color: "rgba(255,255,255,0.4)",
          }}
        >
          <AutoAwesomeIcon sx={{ fontSize: 16 }} />
          <Typography variant="caption" noWrap sx={{ maxWidth: "180px" }}>
            {process.env.REACT_APP_COMMIT_MSG || "v2.1.0"}
          </Typography>
        </Box>
      </Box>
    </Box>
  );

  return (
    <>
      {/* Mobile Top Bar - Only shows on small screens */}
      {isMobile && (
        <AppBar
          position="fixed"
          elevation={0}
          sx={{ background: "#141414", zIndex: 1100, height: "64px" }}
        >
          <Toolbar>
            <IconButton
              color="inherit"
              edge="start"
              onClick={handleDrawerToggle}
              sx={{ mr: 2 }}
            >
              <MenuIcon />
            </IconButton>
            <Typography
              variant="h6"
              noWrap
              component="div"
              sx={{ flexGrow: 1 }}
            >
              <img
                src={fds_logo}
                alt="Logo"
                onClick={() => navigate("/")}
                style={{ height: "35px", cursor: "pointer" }}
              />
            </Typography>
            <IconButton color="inherit" onClick={() => navigate("/prospects")}>
              <Badge variant="dot" color="error" invisible={pendingProspectsCount === 0}>
                <NotificationsIcon />
              </Badge>
            </IconButton>
          </Toolbar>
        </AppBar>
      )}

      {/* Sidebar Drawer */}
      <Box component="nav" sx={{ flexShrink: { md: 0 } }}>
        {/* Mobile Drawer */}
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{ keepMounted: true }} // Better open performance on mobile
          sx={{
            display: { xs: "block", md: "none" },
            "& .MuiDrawer-paper": {
              width: "260px",
              boxSizing: "border-box",
              border: "none",
            },
          }}
        >
          {drawerContent}
        </Drawer>

        {/* Desktop Sidebar */}
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: "none", md: "block" },
            "& .MuiDrawer-paper": {
              width: {
                lg: "260px",
                xl: "300px",
              },
              boxSizing: "border-box",
              background: "#141414",
              borderRight: "1px solid rgba(255,255,255,0.05)",
            },
          }}
          open
        >
          {drawerContent}
        </Drawer>
      </Box>

      {/* Spacer for content to avoid being hidden behind the fixed AppBar/Sidebar */}
      <Box sx={{ height: isMobile ? "64px" : 0 }} />
    </>
  );
}

export default Sidebar;
